"use strict";

import { defaultNameResolver } from "webpack/lib/NamedChunksPlugin";
import ConcatenatedModule from "webpack/lib/optimize/ConcatenatedModule";
import createConsoleLogger from "webpack/lib/logging/createConsoleLogger";
import * as broadcast from "./broadcast.js";
import * as sidebarMap from "./sidebar_map";
import * as util from "./util";
import * as constants from "./constants";

// 本棚エレメント
const container = document.querySelector(".categories");

// サイト設定
const siteSettings = document.getElementById("site-settings");

// iPhone または Safari か
const isiPhoneOrSafari = util.isiPhone() || util.isSafari();

// ページ表示状態の管理
let spa = null;

// タブボタンの管理
let toggleButtonManager = null;

// スクロールバー
let scrollViewport = null;

// アニメーション中のクリック制御
let isClickable = true;

// 他タブの管理
let otherTab = null;

// タブ状態の管理
let otherTabOpen = false;

// 縦スクロール補助アイコン状態の管理
let scrollVerticalHint = false;

// DOMツリー読み込み完了後の初期化処理
document.addEventListener("DOMContentLoaded", init);

/**
 * 初期化処理です
 */
function init() {
  // 本棚エレメントとサイト設定の存在チェック
  if (!container || !siteSettings) return;

  // サーバから受取る共通定義の存在チェック
  if (!gon || !gon.alignment || !gon.pop) location.href = "/500.html";

  // 文字列型で渡るためパース
  gon.alignment = JSON.parse(gon.alignment);
  gon.pop = JSON.parse(gon.pop);

  // 遅延読込み設定
  setLazySizesConfig();

  // リサイズイベント登録
  handleResize();

  // カテゴリ読み込み
  loadCategory();

  // SPA 初期化
  spa = createSPAController();
  toggleButtonManager = util.createToggleButtonManager(spa, setInternalDispatchEvent);

  // クリックイベント登録
  setClickEventModalButtonLinks();
  setClickEventCategory();
  setClickEventMoveLeftRightBtn();
  setClickEventOpenSearchBtn();
  setClickEventOpenHoldBtn();

  // 別タブ間との通信設定
  setBroadcastForOtherTab();

  // リサイズイベント登録
  window.addEventListener("resize", () => handleResize());

  // 画面全体で右クリックからのコンテキストメニューを非表示
  document.addEventListener("contextmenu", (e) => e.preventDefault());

  // サイドバーマップ初期化（内部リンクイベント注入）
  sidebarMap.init(container.querySelectorAll(".main .category"), setInternalDispatchEvent);

  // スクロールバー初期化
  if (util.isAppleDevice()) {
    scrollViewport = container;
  } else {
    const osInstance = util.initScrollbar(container);
    if (osInstance) {
      scrollViewport = osInstance.elements().viewport;
    } else {
      scrollViewport = container;
    }
  }

  // 縦スクロール変換イベント登録
  scrollViewport.addEventListener("wheel", handleWheelForHorizontalScroll, { passive: false });

  // 吹き出しの適用と監視
  document.querySelectorAll("[data-pop]").forEach(addOverlayImage);
  observeDOMMutations();

  // iframe内では右下のボタンを非表示
  if (gon.iframe && JSON.parse(gon.iframe)) {
    const tabButton = document.getElementById("tab-button-container");
    if (tabButton) {
      tabButton.style.display = "none";
    }
  }
}

/**
 * DOM の変更を監視します
 */
function observeDOMMutations() {
  const target = document.querySelector(".categories");
  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.("[data-pop]")) {
          addOverlayImage(node);
        } else if (node.querySelector?.("[data-pop]")) {
          // 子孫要素にも data-pop 属性があれば再帰的に処理
          node.querySelectorAll("[data-pop]").forEach(addOverlayImage);
        }
      });
    });
  }).observe(target, { childList: true, subtree: true });
}

/**
 * 複数棚の SPA 対応のための初期化処理です
 */
function createSPAController(initialPath = null) {
  const viewMap = {};
  const defaultViewSelector = constants.DEFAULT_VIEW_SELECTOR;

  // タブビュー毎の初期マッピング生成
  document.querySelectorAll(`#tab-button-container a[href]`).forEach((link) => {
    const path = link.getAttribute("href");
    const selector = link.dataset.viewSelector;
    viewMap[path] = selector;
  });

  // 横スクロール位置記録用
  const scrollPositions = {};
  let currentViewSelector = null;

  function showView(path, defaultSelector = defaultViewSelector) {
    // 現在ビューのスクロール位置保存
    if (currentViewSelector) {
      const maxScroll = scrollViewport.scrollWidth - scrollViewport.clientWidth;
      scrollPositions[currentViewSelector] = scrollViewport.scrollLeft <= maxScroll ? scrollViewport.scrollLeft : maxScroll;
    }

    // 表示対象決定
    const selector = viewMap[path] || defaultSelector;
    currentViewSelector = selector;

    // 全ビュー非表示
    const allSelectors = [...new Set([...Object.values(viewMap), defaultViewSelector])];
    allSelectors.forEach((sel) => document.querySelector(sel)?.classList.add("hidden"));

    // 対象ビュー表示
    document.querySelector(selector)?.classList.remove("hidden");

    // スクロール復元
    const savedScroll = scrollPositions[selector] || 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollViewport.scrollLeft = savedScroll;
      });
    });
  }

  function navigateTo(path) {
    showView(path);
  }

  function changeView(selector) {
    showView(null, selector);
  }

  function isLinkTab(path) {
    return Object.keys(viewMap).includes(path);
  }

  function getCurrentViewSelector() {
    return currentViewSelector;
  }

  // 初期表示
  showView(initialPath || window.location.pathname);

  return { navigateTo, changeView, isLinkTab, getCurrentViewSelector };
}

/**
 * 吹き出し画像を追加します
 * @param {HTMLElement} element
 */
function addOverlayImage(element) {
  const popIds = element
    .getAttribute("data-pop")
    .split(",")
    .map((id) => parseInt(id.trim(), 10));
  const matchedImages = gon.pop.filter((item) => popIds.includes(item.id));

  // data-pop 属性の値が空であれば終了
  if (matchedImages.length === 0) return;

  const imgContainer = element.querySelector(".spine-image");
  if (!imgContainer) return;

  // 吹き出し画像の設定
  const overlayImg = document.createElement("img");
  overlayImg.classList.add("overlay-pop-img");
  imgContainer.appendChild(overlayImg);

  // 吹き出し画像の位置設定（top を計算）
  function updateTopPosition() {
    requestAnimationFrame(() => {
      const rect = imgContainer.getBoundingClientRect();
      overlayImg.style.top = `${Math.max(-rect.top, -53)}px`;
    });
  }

  // imgContainer の表示状態を監視
  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry, index, array) => {
      if (entry.isIntersecting) {
        updateTopPosition();
        syncAnimation();
      }
    });
  });
  observer.observe(imgContainer);

  // リサイズ時の処理
  window.addEventListener("resize", updateTopPosition);

  // 画像の切り替え処理
  if (matchedImages.length > 1) {
    let index = 0;
    setInterval(() => {
      requestAnimationFrame(() => {
        overlayImg.src = matchedImages[(index = (index + 1) % matchedImages.length)].image;
      });
    }, 1000); //TODO: CSS の点滅アニメーションと、画像切替え処理は異なる処理のためズレる。要検討
  } else {
    overlayImg.src = matchedImages[0].image;
  }

  // 初回実行
  updateTopPosition();
  syncAnimation();
}

/**
 * アニメーションをリセットして再適用します
 */
function syncAnimation() {
  document.querySelectorAll(".overlay-pop-img").forEach((img) => {
    img.style.animation = "none";
    requestAnimationFrame(() => {
      // ここで animation = "none" を適用
      requestAnimationFrame(() => {
        // 次のフレームでアニメーションを再適用
        img.style.animation = "blink 1s steps(1, end) infinite";
      });
    });
  });
}

/**
 * 遅延読込み（横スクロール向け）設定です
 */
function setLazySizesConfig() {
  // lazysizes の設定
  window.lazySizesConfig = {
    expand: 400, // 先読みサイズの設定（10冊程度を目途に先読み）
    hFac: 1.0, // 水平方向の拡張を有効化（横スクロールの場合 1.0 - 1.5 推奨）
    init: true,
  };
}

/**
 * リサイズイベント時に全てのカテゴリと本の要素の調整、左右ボタンの表示/非表示を制御します
 */
function handleResize(categoriesElement = null, booksElement = null) {
  const selectorCategoriesResult = categoriesElement ? categoriesElement : document.querySelectorAll(".category");
  const categories = selectorCategoriesResult && typeof selectorCategoriesResult.length === "number" ? Array.from(selectorCategoriesResult) : selectorCategoriesResult ? [selectorCategoriesResult] : [];
  if (categories.length === 0) return;

  // 実際に使う高さを計算
  const adjustedHeight = util.getAdjustedHeight(container);

  // カテゴリのリサイズ処理
  categories.forEach((el) => resizeCategory(el, adjustedHeight));

  // 本のリサイズ処理
  const selectorBooksResult = booksElement ? booksElement : document.querySelectorAll(".book");
  const books = selectorBooksResult && typeof selectorBooksResult.length === "number" ? Array.from(selectorBooksResult) : selectorBooksResult ? [selectorBooksResult] : [];
  books.forEach((book) => resizeBook(book, adjustedHeight));

  // 左右ボタンの表示制御
  // iframe内では常に非表示とし、それ以外は設定に従います
  const moveBtnId = document.getElementById("move-btn");
  const isIframe = gon.iframe && JSON.parse(gon.iframe);
  const alignment = util.isLandscape() ? siteSettings.dataset.sideControlLandscape : siteSettings.dataset.sideControlPortrait;
  moveBtnId.classList.toggle("hidden", isIframe || alignment == gon.alignment.NONE);
}

/**
 * カテゴリをロードして表示を更新（展開）します
 */
function loadCategory() {
  const loadCategoryId = container?.getAttribute("load-category-id");

  // トップページへのアクセスかカテゴリへのアクセスかのチェック
  if (!loadCategoryId) return;

  const category = document.querySelector(`[data-category-id="${loadCategoryId}"]`);
  if (!category) return;

  // カテゴリ表示
  viewCategory(category.id);
}

/**
 * 指定した親カテゴリの子カテゴリ全てを展開します
 * 展開する条件は親カテゴリの data-is-expanded 属性が true の場合のみです
 * 基本的にgetUrlData()とセットで使用します
 * @param {HTMLElement} parentCategory - 親カテゴリの要素
 */
function expandAllChildCategories(parentCategory) {
  if (!parentCategory) return;

  const isExpand = parentCategory.getAttribute("data-is-expanded") == "true";
  if (!isExpand) return;

  parentCategory.querySelectorAll(`.category`).forEach((category) => {
    const isOpen = util.isOpenCategory(category);
    if (isOpen) return; // 開いている要素には何もしない

    const eleLink = category.querySelector(".category_spine_link");
    const newUrl = eleLink.getAttribute("href");

    // すべてのカテゴリにおいてアニメーションや履歴残しの対象外
    // 順次展開するのでブラウザのURLも更新しない
    getUrlData(category, newUrl, gon.alignment.NONE, false, false);
  });
}

/**
 * 親カテゴリも考慮して指定カテゴリを表示します
 * @param {string} categoryId - 表示するカテゴリID
 */
function viewCategory(categoryId) {
  const categoryElement = document.getElementById(categoryId);
  if (!categoryElement) return;

  // 親要素のカテゴリ探索
  const parentCategoryElements = findAncestorCategories(categoryElement);

  // 対象カテゴリ追加（履歴残し対象）
  parentCategoryElements.push(categoryElement);

  // 対象カテゴリを表示するために親カテゴリから順次開く
  parentCategoryElements.forEach((parentCategory, index, array) => {
    const isOpen = util.isOpenCategory(parentCategory);
    const isLast = index === array.length - 1;
    if (isOpen && !isLast) return; // 開いている要素には何もしない（最後は除く）
    const eleLink = parentCategory.querySelector(".category_spine_link");
    const newUrl = eleLink.getAttribute("href");

    // デバイスの向きに応じた内部リンク制御設定を取得
    const alignment = Number(util.isLandscape() ? siteSettings.dataset.internalLinkControlLandscape : siteSettings.dataset.internalLinkControlPortrait);

    // 親カテゴリはアニメーションや履歴残しの対象外
    getUrlData(parentCategory, newUrl, isLast ? alignment : gon.alignment.NONE, isLast ? true : false);
    if (isLast) {
      // 最後のカテゴリは子カテゴリも展開
      expandAllChildCategories(parentCategory);
    }
  });

  // 別タブ遷移時の考慮
  if (spa) {
    const linkElement = categoryElement.querySelector(".category_spine_link");
    spa.navigateTo(linkElement.getAttribute("href"));
  }
}

/**
 * 指定された階層のルールに基づき、指定の要素をアニメーションで移動させます
 * @param {string} targetLayer - 移動する階層を示す定数
 * @param {HTMLElement} targetElement - 移動対象の要素
 * @param {string} alignment - 寄せ位置の指定
 * @param {number } initialStartTime - タイムアウト用途の開始時間（注：引数指定しないこと）
 */
function moveAnimation(targetLayer, targetElement, alignment = gon.alignment.OPEN_LEFT_ALIGNMENT, initialStartTime = null) {
  // 指定された寄せ位置が無効な場合は処理しない
  if (alignment == gon.alignment.NONE) return;

  // 初回の呼び出しで開始時間を設定
  if (!initialStartTime) initialStartTime = performance.now();

  // 対象要素が非表示の場合、次フレームで再試行
  if (!targetElement || !targetElement.offsetParent) {
    const currentTime = performance.now();
    if (currentTime - initialStartTime > constants.ANIMATION_DURATION.TIMEOUT) {
      console.error("Timeout: Target element is still not visible.");
      return; // タイムアウトした場合は処理を中断
    }
    requestAnimationFrame(() => moveAnimation(targetLayer, targetElement, alignment, initialStartTime));
    return;
  }

  // ローディング開始
  util.showLoader();

  // アニメーション遅延スタート
  setTimeout(() => {
    _startAnimation(targetLayer, targetElement, alignment, () => {
      // ローディング終了
      util.hideLoader();
    });
  }, constants.ANIMATION_DURATION.CATEGORY_OPEN);
}

/**
 * 横スクロールアニメーションを実行する
 * @param {string} targetLayer - 対象レイヤー（CATEGORY / BOOK など）
 * @param {HTMLElement} targetElement - 中央寄せ or 左寄せ対象の要素
 * @param {string} alignment - 寄せ位置の指定
 * @param {Function} [onComplete] - アニメーション完了時のコールバック
 */
function _startAnimation(targetLayer, targetElement, alignment, onComplete = null) {
  // スクロール先の座標を計算
  function calcTargetScroll() {
    void scrollViewport.offsetWidth; // 強制リフロー

    const containerRect = scrollViewport.getBoundingClientRect();
    const elementRect = targetElement.getBoundingClientRect();

    function getCenterPosition() {
      return elementRect.left + scrollViewport.scrollLeft - (containerRect.left + containerRect.width / 2) + elementRect.width / 2;
    }

    function getLeftPosition() {
      return elementRect.left + scrollViewport.scrollLeft - containerRect.left;
    }

    switch (targetLayer) {
      case constants.MULTI_LAYER.CATEGORY:
        return alignment == gon.alignment.OPEN_CENTERING ? getCenterPosition() : getLeftPosition();
      case constants.MULTI_LAYER.BOOK:
        return getCenterPosition();
      default:
        return null;
    }
  }

  // スクロール処理
  function doScroll() {
    const tolerance = 2;
    let isDone = false;
    let targetScroll = calcTargetScroll();

    // finish() の中で即終了するのではなく、しばらく監視を続ける
    function finish(reason) {
      if (isDone) return;
      isDone = true;

      // 検証用ログ出力コード
      // console.log("Scroll finished:", reason, {
      //   targetScroll,
      //   currentScroll: scrollViewport.scrollLeft,
      //   maxScroll: scrollViewport.scrollWidth - scrollViewport.clientWidth,
      // });

      // 通信環境が悪い場合、位置ズレする可能性があるため、ガード処理を追加
      if (reason === constants.ANIMATION_FINISH_REASON.REACHED_TARGET) {
        const guardDuration = isiPhoneOrSafari ? 500 : 250; // ms
        const guardStart = performance.now();

        function guardCheck() {
          const newTarget = calcTargetScroll();
          const current = scrollViewport.scrollLeft;

          if (targetScroll != newTarget && Math.abs(newTarget - current) > tolerance) {
            isDone = false; // 再度スクロール可能に
            targetScroll = newTarget;

            // 補正スクロール（iPhone/Safari はユーザビリティ低下を防ぐため auto 設定）
            scrollViewport.scrollTo({ left: newTarget, behavior: isiPhoneOrSafari ? "auto" : "smooth" });

            // 通常監視に戻す
            requestAnimationFrame(check);
            return;
          }

          // ガード期間内にスクロールが完了していれば onComplete を呼び出す
          if (performance.now() - guardStart < guardDuration) {
            requestAnimationFrame(guardCheck);
          } else {
            if (onComplete) onComplete();
          }
        }

        requestAnimationFrame(guardCheck);
        return;
      }

      if (onComplete) onComplete();
    }

    const maxScroll = scrollViewport.scrollWidth - scrollViewport.clientWidth;

    // スクロールできない場合や既に到達済みなら即完了
    if (scrollViewport.scrollWidth <= scrollViewport.clientWidth) {
      finish(constants.ANIMATION_FINISH_REASON.CANNOT_SCROLL);
      return;
    }
    if (Math.abs(scrollViewport.scrollLeft - targetScroll) < tolerance) {
      finish(constants.ANIMATION_FINISH_REASON.ALREADY_AT_TARGET);
      return;
    }
    if (scrollViewport.scrollLeft >= maxScroll && targetScroll > maxScroll) {
      finish(constants.ANIMATION_FINISH_REASON.ALREADY_AT_MAX);
      return;
    }
    if (scrollViewport.scrollLeft <= 0 && targetScroll < 0) {
      finish(constants.ANIMATION_FINISH_REASON.ALREADY_AT_MIN);
      return;
    }

    // ネイティブ smooth scroll
    scrollViewport.scrollTo({ left: targetScroll, behavior: "smooth" });

    // RAF ポーリングで完了検知
    const start = performance.now();

    function check() {
      const current = scrollViewport.scrollLeft;

      // 指定位置に辿り着いたら終了
      if (Math.abs(current - targetScroll) < tolerance) {
        finish(constants.ANIMATION_FINISH_REASON.REACHED_TARGET);
        return;
      }

      // 端に達したら終了
      const atLeftEdge = current <= tolerance && targetScroll <= tolerance;
      const atRightEdge = current >= maxScroll - tolerance && targetScroll >= maxScroll - tolerance;

      if (atLeftEdge || atRightEdge) {
        finish(constants.ANIMATION_FINISH_REASON.EDGE_REACHED);
        return;
      }

      // 一定時間以上かかったら失敗扱い
      if (performance.now() - start > constants.ANIMATION_DURATION.TIMEOUT) {
        finish(constants.ANIMATION_FINISH_REASON.TIMEOUT);
        return;
      }

      requestAnimationFrame(check);
    }

    requestAnimationFrame(check);
  }

  let hasScrolled = false;
  let stabilityTimer = null;

  // ResizeObserver + タイムアウトで二重呼び出し防止
  const observer = new ResizeObserver(() => {
    clearTimeout(stabilityTimer);

    // 安定したらスクロール実行
    stabilityTimer = setTimeout(() => {
      observer.disconnect();
      if (!hasScrolled) {
        hasScrolled = true;
        requestAnimationFrame(doScroll);
      }
    }, constants.ANIMATION_DURATION.CATEGORY_OPEN);
  });

  observer.observe(targetElement);

  setTimeout(() => {
    if (observer) {
      observer.disconnect();
      if (!hasScrolled) {
        hasScrolled = true;
        requestAnimationFrame(doScroll);
      }
    }
  }, constants.ANIMATION_DURATION.TIMEOUT);
}

/**
 * カテゴリのサイズを調整します
 * @param {HTMLElement} categoryElement - コンテナ要素
 * @param {number} adjustedHeight - class='category' の要素
 */
function resizeCategory(categoryElement, adjustedHeight) {
  if (categoryElement.clientHeight === 0) return;

  const img = categoryElement.querySelector(".spine-image");
  const marginTopPx = categoryElement.getAttribute("data-margin-top");

  // カテゴリの高さを設定
  setSpineHeight(img, adjustedHeight, marginTopPx);

  // 余白設定
  categoryElement.style.paddingTop = `${marginTopPx}px`;

  // 各詳細要素の処理
  categoryElement.querySelectorAll(".category-detail").forEach((element) => {
    element.style.height = img.style.height;
    const adjustedWidth = element.getAttribute("data-detail-width");
    setDetailWidth(element, adjustedWidth, adjustedHeight);
  });
}

/**
 * 本のサイズを調整します
 * @param {HTMLElement} bookElement
 * @param {number} adjustedHeight - class='category' の要素
 */
function resizeBook(bookElement, adjustedHeight) {
  // ブラウザの表示領域と詳細表の高さの基準値から比率を計算
  const rate = adjustedHeight / constants.DEFAULT_IMAGE_HEIGHT;
  if (rate === 0) return;

  const marginTopPx = Math.floor(rate * bookElement.getAttribute("data-margin-top"));

  // 本の背表紙の高さを計算
  const bookSpine = bookElement.querySelector(".spine-image");
  setSpineHeight(bookSpine, adjustedHeight, marginTopPx);

  // 本の詳細の高さを設定
  const bookDetail = bookElement.querySelector(".book-detail");
  bookDetail.style.height = bookSpine.style.height;

  // 帯コメントのフォントサイズを自動調整
  setTimeout(() => autoResizeText(bookSpine), constants.ANIMATION_DURATION.FADE);

  // アイコン表示の切り替え
  const zoomIn = bookElement.querySelector(".zoom-in");
  const zoomOut = bookElement.querySelector(".zoom-out");
  zoomIn?.classList.toggle("hidden", false);
  zoomOut?.classList.toggle("hidden", true);

  // 詳細の横幅を設定
  const adjustedWidth = bookElement.getAttribute("data-detail-width");
  setDetailWidth(bookDetail, adjustedWidth, adjustedHeight);
}

/**
 * 帯コメントのフォントサイズを自動調整
 * @param {*} container
 */
function autoResizeText(container) {
  const el = container.querySelector(".obi-comment");
  if (!el) return; // 帯コメントが存在しない場合は終了

  let fontSize = 16;
  el.style.fontSize = `${fontSize}px`;

  const minSize = 3;

  while ((el.scrollHeight > el.offsetHeight || el.scrollWidth > el.offsetWidth) && fontSize > minSize) {
    fontSize -= 0.5;
    el.style.fontSize = `${fontSize}px`;
  }
}

/**
 * 詳細の幅を設定します
 * @param {HTMLElement} detailElement - 幅を設定する詳細の要素
 * @param {number} adjustedWidth - 調整値（幅）
 * @param {number} adjustedHeight - 調整値（高さ）
 */
function setDetailWidth(detailElement, adjustedWidth, adjustedHeight) {
  const WIDTH_RATE = 0.7;
  const viewportWidth = document.body.clientWidth * WIDTH_RATE;
  const rate = adjustedHeight / constants.DEFAULT_IMAGE_HEIGHT;
  const detailWidth = Math.floor(rate * adjustedWidth);

  detailElement.style.width = `${Math.min(viewportWidth, detailWidth)}px`;
}

/**
 * 背表紙画像の高さを設定します
 * @param {HTMLElement} spineElement - 表紙画像の要素
 * @param {number} clientHeight - ブラウザの表示領域
 * @param {number} marginTop - 表紙上部のマージンサイズ
 */
function setSpineHeight(spineElement, clientHeight, marginTop) {
  const spineHeight = clientHeight - marginTop;
  // 幅を一旦リセットしてから背表紙の高さと幅を更新
  spineElement.style.width = "";
  spineElement.style.height = `${spineHeight}px`;
  requestAnimationFrame(() => {
    spineElement.style.width = "fit-content";
  });
}

/**
 * 別タブ間との通信をセットアップします
 */
function setBroadcastForOtherTab() {
  // 別タブで実行するための関数セット
  const availableFunctions = {
    navigateTo: (linkCategoryId) => {
      viewCategory(linkCategoryId);
    },
  };

  // タブ間の通信をセットアップ
  broadcast.handleBroadcastMessages(availableFunctions);
}

/**
 * 内部リンクのイベントディスパッチです
 * @param {*} internalLinkElement
 * @param {boolean} registerEvent - true ならクリックイベントを登録する
 * @returns {Function|null} クリックイベントハンドラ（registerEvent が false の場合のみ）
 */
function setInternalDispatchEvent(internalLinkElement, registerEvent = true) {
  const internalLink = internalLinkElement.getAttribute("href");

  // 内部リンクに対応するカテゴリの背表紙リンク要素を取得
  const categorySpineLinkElement = document.querySelector(`.category_spine_link[href="${internalLink}"]`);

  // デバイスの向きに応じた内部リンク制御設定を取得
  const internalLinkControlSetting = Number(util.isLandscape() ? siteSettings.dataset.internalLinkControlLandscape : siteSettings.dataset.internalLinkControlPortrait);

  // パンくずリストの更新とアニメーション処理を実行
  const updateMapAndAnimate = (linkElement, categoryElement, alignment) => {
    const spineImageElement = categoryElement.querySelector(".spine-image");
    handleResize(); // リサイズ処理を実行
    moveAnimation(constants.MULTI_LAYER.CATEGORY, spineImageElement, alignment); // アニメーションを実行
    updateHistoryElement(linkElement); // 閲覧履歴更新
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // 内部リンク以外であれば別タブで表示する
    if (!internalLink.startsWith("/")) {
      window.open(internalLink, "_blank");
      return;
    }

    // 別タブでない、かつ対象カテゴリが存在しない場合、鍵付きページ（限定公開）での実行と見做して処理する
    if (!util.isNewTab() && !categorySpineLinkElement) {
      // 別タブが開かれているかチェック
      if (otherTabOpen && otherTab && !otherTab.closed) {
        // 既存の別タブに移動
        otherTab.focus();

        const newTabCategorySpineLinkElement = otherTab.document.querySelector(`.category_spine_link[href="${internalLink}"]`);
        const newTabCategoryElement = newTabCategorySpineLinkElement?.parentElement;

        // 移動したタブ内に対象カテゴリが存在するか判定（縦長の場合は別タブ表示を強制）
        if (!newTabCategoryElement || !util.isLandscape()) {
          window.open(internalLink, "_blank");
        } else {
          // 移動したタブにメッセージを送信して内部リンク移動を実行
          broadcast.callFunctionInOtherTab("navigateTo", [newTabCategoryElement.id]);
        }
      } else {
        // 別タブを開いて内部リンク移動を実行
        otherTab = window.open(internalLink, constants.TATEOKI_TAB_NAME);
        if (otherTab) {
          otherTabOpen = true;
          otherTab.focus();
        } else {
          console.error("Not Found: Failed to display new tab.");
        }
      }

      return;
    }

    // 指定リンク先の本棚を表示（SPA 対応）
    spa.navigateTo(internalLink);
    toggleButtonManager.updateStates(spa.getCurrentViewSelector());

    // 通常のカテゴリ表示処理
    const categoryElement = categorySpineLinkElement?.parentElement;
    if (!categoryElement) return;

    sidebarMap.openToNode(categoryElement.id);

    // 親要素のカテゴリ探索
    const parentCategoryElements = findAncestorCategories(categoryElement);
    parentCategoryElements.forEach((parentCategoryElement) => {
      if (util.isOpenCategory(parentCategoryElement)) return;
      const viewUrl = parentCategoryElement.querySelector(".category_spine_link").getAttribute("href");
      // Apple デバイスでなければ、親カテゴリはアニメーションの対象外
      getUrlData(parentCategoryElement, viewUrl, util.isAppleDevice() ? internalLinkControlSetting : gon.alignment.NONE, false);
    });

    const isOpen = util.isOpenCategory(categoryElement);
    const isFromBrowsingMap = ["history", "category-level"].some((cls) => internalLinkElement.classList.contains(cls));
    const isFromBrowsingCategoryLevel = internalLinkElement.classList.contains("category-level");

    // 既にカテゴリが開いているか判定
    if (isOpen) {
      const alignment = spa.isLinkTab(internalLink) ? gon.alignment.NONE : internalLinkControlSetting;
      viewCategoryLevel(isFromBrowsingCategoryLevel ? internalLinkElement : categorySpineLinkElement);
      updateMapAndAnimate(isFromBrowsingMap ? internalLinkElement : categorySpineLinkElement, categoryElement, alignment);
      return;
    }

    // 階層表示または履歴からのアクセスか判定
    if (isFromBrowsingMap) {
      const booksElement = [...categoryElement.children].find((el) => el.matches(".books"));
      // オープン判定済のためカテゴリと本を表示する
      toggleCategories(categoryElement);
      toggleBooks(booksElement);
      viewCategoryLevel(isFromBrowsingCategoryLevel ? internalLinkElement : categorySpineLinkElement);
      updateMapAndAnimate(internalLinkElement, categoryElement, internalLinkControlSetting);
      return;
    }

    const newUrl = categoryElement.querySelector(".category_spine_link").getAttribute("href");
    getUrlData(categoryElement, newUrl, internalLinkControlSetting);
    expandAllChildCategories(categoryElement);
  };

  // イベントを登録する場合
  if (registerEvent) {
    internalLinkElement.addEventListener("click", handleClick);
    return;
  }

  // イベント登録しない場合、ハンドラ関数を返す
  return handleClick;
}

/**
 * 内部リンクのクリックイベントを設定します
 * @param {HTMLElement} categoryInfoElement - カテゴリ情報要素
 * @param {HTMLElement} booksElement - 本の要素
 */
function setClickEventInternalLinks(categoryInfoElement, booksElement) {
  // カテゴリ詳細の内部リンクを設定する
  categoryInfoElement?.querySelectorAll('.category-detail a[href^="/"]').forEach((el) => setInternalDispatchEvent(el));

  // 本の詳細の内部リンクを設定する
  booksElement?.querySelectorAll('.book-detail a[href^="/"]').forEach((el) => setInternalDispatchEvent(el));
}

/**
 * モーダル上の内部リンクのクリックイベントを設定します
 */
function setClickEventModalButtonLinks() {
  // モーダルの内部リンクを設定する
  const modalWrapper = document.getElementById("modal-wrapper");
  modalWrapper?.querySelectorAll('.entrance a[href^="/"]').forEach((el) => setInternalDispatchEvent(el));
}

/**
 * カテゴリ（索引）のクリックイベントを設定します
 */
function setClickEventCategory(categoryElements = document.querySelectorAll(".category")) {
  categoryElements.forEach(function (element) {
    const eleLink = element.querySelector(".category_spine_link");
    if (!eleLink) return;

    const newUrl = eleLink.getAttribute("href");

    eleLink.addEventListener("click", function (event) {
      event.preventDefault(); // デフォルトのクリック動作をキャンセル

      if (!isClickable) return; // アニメーション中はクリックを無効化（200ms）

      const isOpen = util.isOpenCategory(element);
      if (isOpen) {
        // 子カテゴリをすべて閉じる
        const categoryChildElements = [...element.querySelectorAll(".category")].reverse();
        categoryChildElements.forEach((categoryChildElement) => {
          if (!util.isOpenCategory(categoryChildElement)) return;
          const booksElement = [...categoryChildElement.children].find((el) => el.matches(".books"));
          toggleCategories(categoryChildElement);
          toggleBooks(booksElement);
        });
        // サイドバーマップの同期処理
        sidebarMap.closeAllChildren(element.id);
      } else {
        // サイドバーマップの同期処理
        sidebarMap.openToNode(element.id);
      }

      // デバイスの向きに応じたカテゴリ制御設定を取得
      const categoryControlSetting = util.isLandscape() ? siteSettings.dataset.categoryControlLandscape : siteSettings.dataset.categoryControlPortrait;

      // URLからカテゴリデータを取得して表示する
      getUrlData(element, newUrl, categoryControlSetting);
      // クリックしたカテゴリの子カテゴリを自動展開
      expandAllChildCategories(element);
    });
  });
}

/**
 * カテゴリ（索引）の左右移動ボタンのクリックイベントを設定します
 */
function setClickEventMoveLeftRightBtn() {
  const moveIdElement = document.querySelector("#move-btn");
  const buttons = moveIdElement.querySelectorAll(".image-btn");

  buttons.forEach((button) => {
    const isLeftArrow = button.firstElementChild.classList.contains("left-arrow");
    const isRightArrow = button.firstElementChild.classList.contains("right-arrow");

    if (isLeftArrow || isRightArrow) {
      button.addEventListener("click", handleArrowClick.bind(null, isLeftArrow));
    }
  });
}

/**
 * 検索ボタンのクリックイベントを設定します
 */
function setClickEventOpenSearchBtn() {
  toggleButtonManager.register({
    btnId: "open-search",
    labelId: "tab-search-button",
    defaultLabel: "探す",
    activeLabel: "戻る",
    requiredSelector: '.category_spine_link[href="/search"]',
  });
}

/**
 * 気になるボタンのクリックイベントを設定します
 */
function setClickEventOpenHoldBtn() {
  toggleButtonManager.register({
    btnId: "open-hold",
    labelId: "tab-hold-button",
    defaultLabel: "気になる",
    activeLabel: "戻る",
  });
}

/**
 * 左右矢印クリックイベントのハンドラーです
 * @param {boolean} isLeftArrow - 左矢印かどうかのフラグ
 * @param {Event} event - クリックイベント
 */
function handleArrowClick(isLeftArrow, event) {
  event.preventDefault(); // デフォルトのクリック動作をキャンセル

  const sideControlSetting = util.isLandscape() ? siteSettings.dataset.sideControlLandscape : siteSettings.dataset.sideControlPortrait;

  const position = sideControlSetting == gon.alignment.OPEN_CENTERING ? constants.ELEMENT_POSITION.CENTER : constants.ELEMENT_POSITION.LEFT;

  let referencePointElement = util.getElementAtPosition(position);
  if (!referencePointElement) return;

  const isReferencePointCategoryInfo = referencePointElement.classList.contains("category-info");
  const isReferencePointBooks = referencePointElement.classList.contains("books");

  let targetElement;

  if (isLeftArrow) {
    targetElement = handleLeftArrow(referencePointElement, position);
  } else {
    targetElement = handleRightArrow(referencePointElement, isReferencePointCategoryInfo, isReferencePointBooks);
  }

  if (!util.isCategory(targetElement)) return;

  // サイドバーマップ内の対象を開く
  sidebarMap.openToNode(targetElement.id);

  // カテゴリが閉じている場合は開く
  if (sideControlSetting == gon.alignment.OPEN_LEFT_ALIGNMENT || sideControlSetting == gon.alignment.OPEN_CENTERING) {
    if (!util.isOpenCategory(targetElement)) {
      const linkElement = targetElement.querySelector(".category_spine_link");
      const newUrl = linkElement.getAttribute("href");
      getUrlData(targetElement, newUrl, sideControlSetting);
      // クリックしたカテゴリの子カテゴリを自動展開
      expandAllChildCategories(targetElement);
      return;
    }
  }

  targetElement = targetElement.querySelector(".category_spine_link");
  moveAnimation(constants.MULTI_LAYER.CATEGORY, targetElement, sideControlSetting);
  updateHistoryElement(targetElement); // 閲覧履歴更新
  viewCategoryLevel(targetElement); // 階層表示
}

/**
 * 左矢印クリック時の処理を行います
 * @param {Element} referencePointElement - 参照ポイントとなる要素
 * @param {string} position - 位置の指定 ('CENTER' または 'LEFT')
 * @returns {Element} - 移動先の要素
 */
function handleLeftArrow(referencePointElement, position) {
  // 規定位置が左側であり、参照要素がカテゴリであり、範囲内にある場合
  if (position === constants.ELEMENT_POSITION.LEFT && util.isCategory(referencePointElement) && util.isElementWithinBounds(referencePointElement, position)) {
    return referencePointElement;
  }

  // 左隣の要素を初期ターゲットとして設定
  let targetElement = referencePointElement.previousElementSibling;

  // ターゲットがカテゴリの場合、子カテゴリを探索
  if (util.isCategory(targetElement)) {
    // 子カテゴリを逆順で探索し、表示されているものを探す
    while (targetElement) {
      const viewChildCategory = findVisibleChildCategory(targetElement, true);
      if (!viewChildCategory) break;
      targetElement = viewChildCategory;
    }
  } else {
    // ターゲットがカテゴリでない場合は親要素をターゲットに設定
    targetElement = referencePointElement.parentElement;
  }

  return targetElement;
}

/**
 * 右矢印クリック時の処理を行います
 * @param {Element} referencePointElement - 参照ポイントとなる要素
 * @param {boolean} isReferencePointCategoryInfo - 参照ポイントがカテゴリ詳細かどうか
 * @param {boolean} isReferencePointBooks - 参照ポイントが背表紙かどうか
 * @returns {Element} - 移動先の要素
 */
function handleRightArrow(referencePointElement, isReferencePointCategoryInfo, isReferencePointBooks) {
  let targetElement;

  if (isReferencePointBooks) {
    targetElement = findNextVisibleCategorySibling(referencePointElement);
  } else {
    targetElement = isReferencePointCategoryInfo ? referencePointElement.parentElement.nextElementSibling : referencePointElement.nextElementSibling;

    const viewChildCategory = findVisibleChildCategory(isReferencePointCategoryInfo ? referencePointElement.parentElement : referencePointElement);
    if (viewChildCategory) targetElement = viewChildCategory;

    if (!util.isCategory(targetElement) && !viewChildCategory) {
      targetElement = findNextVisibleCategorySibling(referencePointElement);
    }
  }

  return targetElement;
}

/**
 * 子カテゴリを探索して表示されている要素を取得します
 * @param {Element} categoryElement - カテゴリ要素
 * @param {boolean} shouldReverse - 要素を逆順で探索するか
 * @returns {Element} - 表示されている子カテゴリ要素
 */
function findVisibleChildCategory(categoryElement, shouldReverse = false) {
  const childrenArray = shouldReverse ? [...categoryElement.children].reverse() : [...categoryElement.children];
  return childrenArray.find((child) => util.isViewCategory(child));
}

/**
 * 親要素の次のカテゴリを再帰的に探索し、表示されている要素を取得します
 * @param {Element} element - 検索対象要素
 * @returns {Element} - 表示されている親カテゴリの次の要素
 */
function findNextVisibleCategorySibling(element) {
  let parentNextElement = element.parentElement.nextElementSibling;

  while (parentNextElement) {
    if (util.isViewCategory(parentNextElement)) return parentNextElement;
    if (!parentNextElement.classList.contains("books")) return null;
    parentNextElement = parentNextElement.parentElement.nextElementSibling;
  }

  return null;
}

/**
 * 指定された要素の親要素から class="category" を持つ要素をすべて取得します
 *
 * @param {HTMLElement} element - 探索を開始する要素
 * @returns {HTMLElement[]} 親要素の配列
 */
function findAncestorCategories(element) {
  const parentCategories = [];
  let parent = element.parentElement; // 最初の要素の親を取得

  // 親要素で class="category" を持つものを探す
  while (parent) {
    if (util.isCategory(parent)) {
      parentCategories.push(parent);
    }
    parent = parent.parentElement; // さらに上の親要素を探索
  }

  // 配列を逆順にする
  return parentCategories.reverse();
}

/**
 * カテゴリクリック時にデータを取得して表示します
 * 呼び出し元で await を使用して非同期処理を待機することが可能です
 * expandAllChildCategoriesと組み合わせて使用することを検討してください
 * @param {*} eleCategory - .category の要素
 * @param {*} newUrl - .category_spine_link 要素の href 属性の値
 * @param {*} alignment - 移動アニメーションの寄せ位置
 * @param {*} isHistoryUpdate - パンくずリスト（履歴）更新するか
 * @param {*} isUrlUpdate - ブラウザのURLを.categoryへ更新するか

 */
async function getUrlData(eleCategory, newUrl, alignment, isHistoryUpdate = true, isUrlUpdate = true) {
  // 要素の取得
  const eleSpineImage = eleCategory.querySelector(".spine-image");
  const eleCategoryInfo = eleCategory.querySelector(".category-info");
  const eleBooks = [...eleCategory.children].find((el) => el.matches(".books"));

  // iframeによるアクセス元の情報
  const referer = container.getAttribute("referer");

  // 履歴を更新してURLを変更する
  // === issue#392,453の暫定対応 ここから ===
  // urlにクエリパラメータpを追加
  const p = eleCategory.querySelector(".category_spine_link").dataset.p;
  let params = p ? "?p=" + p : "";
  params += referer ? (params ? "&referer=" + referer : "?referer=" + referer) : "";
  newUrl += params;
  const community_category_id = eleCategory.getAttribute("data-community-category-id");

  // === issue#392,453の暫定対応 ここまで ===
  if (isUrlUpdate) history.replaceState(null, "", newUrl);

  // 本の詳細で気になるボタンを表示するか
  const holdAvailable = container.getAttribute("hold-available");

  // アクセス元のドメインに従ったテンプレートパスを取得
  const domain_id = container.getAttribute("domain-id");
  const template_path = container.getAttribute("template-path");

  const restricted_access = container.getAttribute("restricted-access");

  // カテゴリの詳細情報と本がすでに読み込まれているかチェック
  if (!eleCategory.classList.contains("data-loaded")) {
    // ローディング開始
    util.showLoader();

    // Ajaxでデータを取得
    await new Promise((resolve, reject) => {
      $.ajax({
        type: "GET",
        url: newUrl,
        data: {
          community_category_id: community_category_id,
          hold_available: holdAvailable,
          referer: referer,
          template_path: template_path,
          domain_id: domain_id,
          restricted_access: restricted_access,
        },
        dataType: "script",
        beforeSend: function () {
          // リクエスト送信前の処理
        },
        success: function () {
          // 成功時の処理：HTMLを挿入し、各種イベントを設定
          eleCategoryInfo.innerHTML = cateInfoHTML;
          eleBooks.innerHTML = booksHTML;

          // データ取得時の初回表示
          toggleCategories(eleCategory);
          toggleBooks(eleBooks);

          // カテゴリ詳細に配置された検索フォームのクリックイベント設定
          setClickEventSearchForm(eleCategory);

          // 本のクリックイベント設定と内部リンク設定
          setClickEventBook(eleBooks);
          setClickEventInternalLinks(eleCategoryInfo, eleBooks);

          // 画面のリサイズ処理
          handleResize();
        },
        error: function (data) {
          // エラー発生時の処理
          reject(err);
        },
        complete: function () {
          // 移動アニメーションの実行
          if (alignment != gon.alignment.NONE) {
            moveAnimation(constants.MULTI_LAYER.CATEGORY, eleSpineImage, alignment);
          }

          // ローディング終了
          util.hideLoader();

          if (isHistoryUpdate) {
            updateHistoryElement(eleSpineImage.parentElement); // 閲覧履歴更新
            viewCategoryLevel(eleSpineImage.parentElement); // 階層表示
          }

          // カテゴリの詳細、本情報を読み込み済みとしてマーク
          eleCategory.classList.add("data-loaded");

          resolve();
        },
      });
    });
  } else {
    // 読み込み済みの場合は表示を切り替えるのみ
    toggleCategories(eleCategory);
    toggleBooks(eleBooks);
    handleResize();

    // 表示切替後の結果を取得
    let isOpen = util.isOpenCategory(eleCategory);

    // 移動アニメーションの実行
    if (isOpen && alignment != gon.alignment.NONE) {
      // 開いている場合かつ移動アニメーション設定されている場合
      moveAnimation(constants.MULTI_LAYER.CATEGORY, eleSpineImage, alignment);
    }

    // パンくずリスト更新
    if (isOpen && isHistoryUpdate) {
      updateHistoryElement(eleSpineImage.parentElement); // 閲覧履歴更新
      viewCategoryLevel(eleSpineImage.parentElement); // 階層表示
    }
  }
}

/**
 * カテゴリの表示（詳細、子要素含む）を切り替えます
 * （.category の data-display-detail の値が true の場合表示します）
 * @param {HTMLElement} categoryElement - .category の要素
 */
function toggleCategories(categoryElement) {
  const shouldDisplayDetails = categoryElement.getAttribute("data-display-detail") == "true";
  const categoryInfoElement = categoryElement.querySelector(".category-info");
  const detailElement = categoryInfoElement.querySelector(".category-detail");

  // .category クラスを持つ直下の子要素のみをフィルタリング
  const directChildCategories = [...categoryElement.children].filter((child) => util.isCategory(child));

  // 子カテゴリの表示・非表示
  directChildCategories.forEach((childCategoryElement) => {
    const isView = util.isViewCategory(childCategoryElement);
    childCategoryElement.style.display = isView ? "" : "flex";
    if (isView) {
      // カテゴリの lazyloaded クラスを置換（次回表示時のアニメーション対応のため）
      childCategoryElement.querySelectorAll(".lazyloaded").forEach((imgElement) => {
        imgElement.classList.replace("lazyloaded", "lazyload");
      });
    }
  });

  if (shouldDisplayDetails) {
    // カテゴリ詳細が表示される場合
    const isOpen = Boolean(categoryInfoElement.style.maxWidth);

    // カテゴリ詳細の表示/非表示の切り替え（高解像度ディスプレイ向けに100px余裕を持たせる）
    const detailWidth = parseInt(detailElement.getAttribute("data-detail-width"), 10) + 100 + "px";
    categoryInfoElement.style.maxWidth = isOpen ? "" : detailWidth; // 詳細開閉アニメーション

    // カテゴリの詳細に含まれるメタ情報取得対象のURLからメタ情報を取得
    getMetaInfo(detailElement);
  } else {
    // カテゴリ詳細が非表示の場合
    detailElement.style.display = "none";
  }
}

/**
 * カテゴリに登録されている本の表示切り替えを実行します
 * @param {HTMLElement} booksElement - .category要素内の.books要素
 */
function toggleBooks(booksElement) {
  const isOpen = Boolean(booksElement.style.maxWidth);

  // 本の詳細をすべて閉じる関数
  function closeAllBookDetails(booksElement) {
    booksElement.querySelectorAll(".book-detail").forEach((detail) => {
      detail.style.display = "none";
    });
    booksElement.querySelectorAll(".zoom, .zoom .zoom-in, .zoom .zoom-out").forEach((el) => el.classList.toggle("hidden", true));
    // 本の lazyloaded クラスを置換（次回表示時のアニメーション対応のため）
    booksElement.querySelectorAll(".lazyloaded").forEach((imgElement) => {
      imgElement.classList.replace("lazyloaded", "lazyload");
    });
    booksElement.querySelectorAll(".obi-comment").forEach((obiElement) => {
      obiElement.classList.remove("visible");
    });
    booksElement.style.maxWidth = "";
    booksElement.style.display = "none";
  }

  // 本の詳細をすべて開く関数
  function openAllBookDetails(booksElement) {
    let booksWidth = 0;
    // 本の全幅（背表紙のみ）を取得
    booksElement.querySelectorAll(".book").forEach((detail) => {
      booksWidth += detail.offsetWidth;
    });
    booksElement.querySelectorAll(".obi-comment").forEach((obiElement) => {
      setTimeout(() => {
        obiElement.classList.add("visible");
      }, constants.ANIMATION_DURATION.CATEGORY_OPEN);
    });
    booksElement.style.display = "flex";
    if (booksWidth != 0) {
      booksElement.style.maxWidth = booksWidth + "px";
      isClickable = false;
      setTimeout(() => {
        booksElement.style.maxWidth = "none"; // 本の詳細表示の考慮
        isClickable = true;
        booksElement.querySelectorAll(".zoom, .zoom .zoom-in").forEach((el) => el.classList.toggle("hidden", false));
      }, constants.ANIMATION_DURATION.CATEGORY_OPEN); // アニメーション時間待機
    } else {
      booksElement.style.maxWidth = "none";
    }
  }

  if (isOpen) {
    // 本をすべて閉じる
    closeAllBookDetails(booksElement);
  } else {
    // カテゴリ内の本を開く
    openAllBookDetails(booksElement);
  }
}

/**
 * 本に纏わるクリックイベントを設定します
 * （.category の data-display-detail の値が true の場合表示）
 * @param {HTMLElement} booksElement - .category要素内の.books要素（.book も可）
 */
function setClickEventBook(booksElement) {
  // 本の詳細表示を切り替える関数
  function toggleBookDetail(bookElement, bookDetailElement) {
    const zoom = bookElement.querySelector(".zoom");
    const isOpen = bookDetailElement.style.display == "block";

    // 開閉状態を切り替え
    bookDetailElement.style.display = isOpen ? "none" : "block";

    if (zoom) {
      const zoomIn = zoom.querySelector(".zoom-in");
      const zoomOut = zoom.querySelector(".zoom-out");
      zoom.classList.toggle("hidden", isOpen);
      zoomIn?.classList.toggle("hidden", isOpen);
      zoomOut?.classList.toggle("hidden", !isOpen);
    }

    if (isOpen) {
      // 閉じるときは対象の本をリサイズして終了
      handleResize(null, bookElement);
      return;
    }

    // 開いたときの処理
    moveAnimation(constants.MULTI_LAYER.BOOK, bookDetailElement);
    getMetaInfo(bookDetailElement);
    // fetchBookExtras(bookElement, bookDetailElement);
    viewScrollArrow(bookDetailElement);
  }

  // ズームコントロールの制御
  function setZoomControl(bookElement, bookSpineElement, bookDetailElement) {
    const zoomInButton = bookElement.querySelector(".zoom-in");
    const zoomOutButton = bookElement.querySelector(".zoom-out");
    if (!zoomInButton || !zoomOutButton) return;

    function handleZoomIn() {
      const WIDTH_RATE = 0.85;
      const HEIGHT_MARGIN = 25;

      // 表紙のサイズを取得
      const rect = bookDetailElement.getBoundingClientRect();
      const bookDetailWidth = rect.width;
      const bookDetailHeight = rect.height;

      // NaN や 0 を避ける
      if (!bookDetailWidth || !bookDetailHeight) return;

      // リサイズ対象の幅と高さの比率を計算
      const aspectRatio = bookDetailWidth / bookDetailHeight;

      // 利用可能な最大値を取得
      const usableMaxWidth = window.innerWidth * WIDTH_RATE;
      const usableMaxHeight = util.getAdjustedHeight(container) - HEIGHT_MARGIN;

      // デバイスに合わせた最大値のサイズを計算
      const resizedWidthByMaxHeight = usableMaxHeight * aspectRatio;
      const resizedHeightByMaxWidth = usableMaxWidth / aspectRatio;

      // 新しい幅と高さを決定
      let newWidth, newHeight;
      if (resizedHeightByMaxWidth <= usableMaxHeight) {
        newWidth = usableMaxWidth;
        newHeight = resizedHeightByMaxWidth;
      } else {
        newWidth = resizedWidthByMaxHeight;
        newHeight = usableMaxHeight;
      }

      // 本詳細のサイズを更新
      bookDetailElement.style.width = `${newWidth}px`;
      bookDetailElement.style.height = `${newHeight}px`;

      // 幅を一旦リセットしてから背表紙の高さと幅を更新
      bookSpineElement.style.width = "";
      bookSpineElement.style.height = `${newHeight}px`;
      requestAnimationFrame(() => {
        bookSpineElement.style.width = "fit-content";
      });
    }

    zoomInButton.addEventListener("click", (event) => {
      event.preventDefault();
      // 本のサイズを拡大
      handleZoomIn();
      // 本の中心移動
      moveAnimation(constants.MULTI_LAYER.BOOK, bookDetailElement);
      // アイコン表示の切り替え
      zoomInButton.classList.toggle("hidden", true);
      zoomOutButton.classList.toggle("hidden", false);
    });

    zoomOutButton.addEventListener("click", (event) => {
      event.preventDefault();
      // 元のサイズに戻す
      handleResize(null, bookElement);
    });
  }

  // お気に入りボタンの制御
  function setClickEventFavorite(e) {
    e.preventDefault();
    //TODO: お気に入り登録処理を実装
  }

  // 本の詳細表示内でボタンクリック時の共通のスクロール＆ハイライト処理
  function scrollAndHighlightInBookDetail(bookDetail, targetSelector) {
    const btnGroup = bookDetail.querySelector(".btn-group");
    const targetText = bookDetail.querySelector(targetSelector);

    const btnGroupHeight = btnGroup?.offsetHeight || 0;

    // bookDetail内での targetText の相対位置を取得（10px マージン）
    const targetTop = targetText.offsetTop - btnGroupHeight - 10;

    // スクロール移動
    bookDetail.scrollTo({
      top: targetTop,
      behavior: "smooth",
    });

    // スクロール後にハイライト
    setTimeout(() => {
      targetText.classList.add("highlight");
      setTimeout(() => {
        targetText.classList.remove("highlight");
      }, 1200);
    }, 300);
  }

  // 購入ボタンの制御
  function setClickEventPurchase(bookDetail, event) {
    event.preventDefault();
    scrollAndHighlightInBookDetail(bookDetail, ".purchase-text");
  }

  // お試しボタンの制御
  function setClickEventSample(bookDetail, event) {
    event.preventDefault();
    scrollAndHighlightInBookDetail(bookDetail, ".sample-text");
  }

  // 気になるボタンの制御
  async function setClickEventHold(eleBook, event) {
    event.preventDefault();

    // すでに処理済みなら何もしない
    if (eleBook.classList.contains("is-held")) return;

    const holdCategoryLinkElement = document.querySelector(`.category_spine_link[href="/hold"]`);
    if (!holdCategoryLinkElement) return;

    const holdCategoryElement = holdCategoryLinkElement.closest(".category");
    const holdBooksContainer = holdCategoryElement.querySelector(".books");

    // 気になるカテゴリを開く
    if (!util.isOpenCategory(holdCategoryElement)) {
      const viewUrl = holdCategoryLinkElement.getAttribute("href");
      await getUrlData(holdCategoryElement, viewUrl, gon.alignment.NONE, false);
    }

    // コピー元のボタンをグレーアウトして無効化
    const holdInput = eleBook.querySelector(".hold-form input");
    holdInput.classList.add("is-held");

    // コピー元のズームボタンを非表示
    eleBook.querySelectorAll(".zoom, .zoom .zoom-in, .zoom .zoom-out").forEach((el) => el.classList.toggle("hidden", true));

    // book を複製
    const clone = eleBook.cloneNode(true);

    // コピー先の本を閉じる
    const bookDetail = clone.querySelector(".book-detail");
    bookDetail.style.display = "none";

    // コピー先のスクロール補助アイコンを削除
    bookDetail.querySelector(".scroll-arrow")?.remove();

    // クローンをコピー先に追加
    insertBeforeLast(clone, holdBooksContainer);

    // クリックイベントを設定
    setClickEventBook(clone);

    // 内部リンクにクリックイベントを設定
    setClickEventInternalLinks(null, clone);

    handleResize();

    // コピー元のズームボタンを表示
    eleBook.querySelectorAll(".zoom, .zoom .zoom-in").forEach((el) => el.classList.toggle("hidden", false));
  }

  // 最後から１つ前に挿入
  function insertBeforeLast(clone, container) {
    const children = container.children;
    if (children.length >= 1) {
      container.insertBefore(clone, children[children.length - 1]);
    } else {
      container.appendChild(clone);
    }
  }

  const allBookElement = Array.from(booksElement.querySelectorAll(".book"));
  if (booksElement.matches(".book")) {
    allBookElement.unshift(booksElement);
  }

  allBookElement.forEach((eleBook) => {
    const bookSpine = eleBook.querySelector(".spine-image");
    const bookDetail = eleBook.querySelector(".book-detail");

    // 本詳細内のボタン
    const favoriteForm = bookDetail?.querySelector(".favorite-form input");
    const purchaseForm = bookDetail?.querySelector(".purchase-form input");
    const sampleForm = bookDetail?.querySelector(".sample-form input");
    const holdForm = bookDetail?.querySelector(".hold-form input");

    // 本詳細の表示・非表示（true, false）
    const showDetail = eleBook.getAttribute("data-display-details");

    if (showDetail == "true") {
      setZoomControl(eleBook, bookSpine, bookDetail);
      bookSpine.addEventListener("click", () => toggleBookDetail(eleBook, bookDetail));
      favoriteForm?.addEventListener("click", setClickEventFavorite);
      purchaseForm?.addEventListener("click", (e) => setClickEventPurchase(bookDetail, e));
      sampleForm?.addEventListener("click", (e) => setClickEventSample(bookDetail, e));
      holdForm?.addEventListener("click", (e) => setClickEventHold(eleBook, e));
    }
  });
}

/**
 * 縦スクロールの補助表示が必要な場合に表示します
 * @param {HTMLElement} scrollBox
 */
function viewScrollArrow(scrollBox) {
  // アイコン表示済みの場合は処理しない
  if (scrollBox.querySelector(".scroll-arrow")) return;

  // スクロール領域が存在しなければ処理しない
  if (!checkScroll()) return;

  // すでにスクロールされていたら処理しない
  if (scrollBox.scrollTop > 0) return;

  const svgNS = "http://www.w3.org/2000/svg";
  const scrollArrow = document.createElementNS(svgNS, "svg");

  // 縦スクロール三角形生成
  createFixedTriangle(scrollArrow);

  // 縦スクロール三角形を表示
  scrollArrow.style.opacity = "1";

  // 縦スクロール補助アイコンが一度も消されていなければ生成
  if (!scrollVerticalHint) createFixedScrollHint();

  // スクロールされたら非表示にする
  const scrollHandler = function () {
    // フェードアウトのアニメーションを適用してから削除
    scrollArrow.style.opacity = 0;
    setTimeout(() => scrollArrow?.remove(), constants.ANIMATION_DURATION.FADE);
    scrollVerticalHint = true;
    // スクロールヒントを非表示にする
    const scrollHint = scrollBox.querySelector("#vertical-scroll-hint");
    scrollHint?.classList.remove("visible");
    // イベント削除
    scrollBox.removeEventListener("scroll", scrollHandler);
  };

  // スクロールイベントを監視
  scrollBox.addEventListener("scroll", scrollHandler);

  // スクロール領域の存在チェック
  function checkScroll() {
    return scrollBox.scrollHeight > scrollBox.clientHeight;
  }

  // 三角形を生成する関数
  function createFixedTriangle(scrollArrow) {
    scrollArrow.setAttribute("width", "100");
    scrollArrow.setAttribute("height", "20");
    scrollArrow.setAttribute("viewBox", "0 0 100 20");
    scrollArrow.classList.add("scroll-arrow");

    // 塗りつぶし用の三角形
    const fillPolygon = document.createElementNS(svgNS, "polygon");
    fillPolygon.setAttribute("points", "50,18 98,1 2,1");
    fillPolygon.setAttribute("fill", "#f0e68c");
    fillPolygon.setAttribute("opacity", "0.8");

    // 枠線用の三角形
    const strokePolygon = document.createElementNS(svgNS, "polygon");
    strokePolygon.setAttribute("points", "50,20 100,0 0,0");
    strokePolygon.setAttribute("stroke", "black");
    strokePolygon.setAttribute("stroke-width", "1");
    strokePolygon.setAttribute("fill", "none");

    // SVGにポリゴンを追加
    scrollArrow.appendChild(strokePolygon);
    scrollArrow.appendChild(fillPolygon);

    // コンテナにSVGを追加
    scrollBox.appendChild(scrollArrow);
  }

  // スクロールヒントを作成する関数
  function createFixedScrollHint() {
    // 作成済の場合はその要素を返す
    const oldScrollHint = scrollBox.querySelector("#vertical-scroll-hint");
    if (oldScrollHint) return;

    const scrollHint = document.createElement("div");
    scrollHint.id = "vertical-scroll-hint";
    scrollHint.classList.add("visible");

    const scrollHintImg = document.createElement("img");
    scrollHintImg.src = constants.IMAGE_PATH.VERTICAL_SCROLL_HINT;

    // コンテナに縦スクロール補助アイコンを追加
    scrollHint.appendChild(scrollHintImg);
    scrollBox.appendChild(scrollHint);
  }
}

/**
 * 閲覧履歴を更新します
 * @param {HTMLElement} targetLinkElement - 履歴更新時の対象要素
 */
function updateHistoryElement(targetLinkElement) {
  // 履歴要素を作成する関数
  function createHistoryNode(text, href = "#") {
    const li = document.createElement("li");
    li.className = "jstree-node";

    const icon = document.createElement("i");
    icon.className = "jstree-icon jstree-ocl";
    li.appendChild(icon);

    const anchor = document.createElement("a");
    anchor.className = "jstree-anchor history";
    anchor.href = href;
    anchor.textContent = text;
    li.appendChild(anchor);

    // クリック時の動作登録
    setInternalDispatchEvent(anchor);

    return li;
  }

  // パンくずリスト（階層表示）と閲覧履歴経由の更新は除外
  const isNotUpdate = ["history", "category-level"].some((cls) => targetLinkElement.classList.contains(cls));
  if (isNotUpdate) return;

  // 最終閲覧履歴と更新対象の一致判定
  const treeUlContainer = document.querySelector("#historymap-structure ul");
  const firstLi = treeUlContainer.firstElementChild;
  const lastHistory = firstLi?.querySelector("a.history")?.getAttribute("href") || null;

  // 更新対象のリンクと最終履歴が同じ場合は何もしない
  if (lastHistory == targetLinkElement.getAttribute("href")) return;

  // 履歴要素の作成
  const text = targetLinkElement.getAttribute("data-display-name");
  const href = targetLinkElement.getAttribute("href");
  const newLiNode = createHistoryNode(text, href);

  // 履歴要素追加
  treeUlContainer.insertBefore(newLiNode, treeUlContainer.firstChild);
}

/**
 * カテゴリ階層を表示します
 * @param {*} targetLinkElement
 */
function viewCategoryLevel(targetLinkElement) {
  // categories クラスのある要素まで探索し階層情報を取得
  function collectAncestorLinks(startElement) {
    const results = [];
    let current = startElement;

    while (current && !current.classList.contains("categories")) {
      const parent = current.parentElement;
      if (!parent) break;

      // 同階層（=親要素の子）の中から category_spine_link を探索
      const siblings = Array.from(parent.children);
      for (const sibling of siblings) {
        if (sibling.classList.contains("category_spine_link")) {
          const href = sibling.getAttribute("href");
          const displayName = sibling.getAttribute("data-display-name");

          if (href && displayName) {
            results.push({ href, displayName });
          }
        }
      }

      current = current.parentElement;
    }

    return results;
  }

  const breadcrumbElement = document.getElementById("breadcrumb");
  const breadcrumbOlElement = breadcrumbElement.firstElementChild;

  let isUpdate = true;
  if (targetLinkElement.classList.contains("category-level")) isUpdate = false;

  if (isUpdate) {
    // 初期化
    breadcrumbOlElement.innerHTML = "";

    // 階層情報取得
    const collectedLinks = collectAncestorLinks(targetLinkElement);
    if (collectedLinks.length === 0) return;

    const fragment = document.createDocumentFragment();

    collectedLinks.reverse().forEach((linkElement, index, array) => {
      const liTagElement = document.createElement("li");

      const isLast = index === array.length - 1;
      if (isLast) liTagElement.classList.add("selected");

      const aTagElement = Object.assign(document.createElement("a"), {
        href: linkElement.href,
        textContent: linkElement.displayName,
        className: "category-level",
      });

      // クリックイベントを設定
      setInternalDispatchEvent(aTagElement);

      // 要素追加
      liTagElement.appendChild(aTagElement);
      fragment.appendChild(liTagElement);
    });

    // パンくずリストの要素を更新
    breadcrumbOlElement.appendChild(fragment);

    // パンくずの右寄せ処理（再描画後）
    requestAnimationFrame(() => (breadcrumbOlElement.scrollLeft = breadcrumbOlElement.scrollWidth));
  } else {
    // 対象要素のみ selected クラス追加
    breadcrumbOlElement.childNodes.forEach((liElement) => {
      liElement.classList.remove("selected");
      if (liElement == targetLinkElement.closest("li")) liElement.classList.add("selected");
    });
  }
}

/**
 * 子要素がスクロール可能な親要素内に存在するかを検出します
 * @param {HTMLElement} childElement - 子要素
 * @param {HTMLElement} parentElement - 親要素
 * @returns {boolean} - 子要素がスクロール可能な親要素内に存在する場合はtrue、そうでない場合はfalse
 */
function isChildInScrollableParent(childElement, parentElement) {
  let currentElement = childElement;

  // 要素が縦スクロール可能かどうかを判定する関数
  function isHeightScrollable(element) {
    return element.scrollHeight > element.clientHeight;
  }

  // 子要素が親要素の中にある限り、上位の親要素に対してスクロール可能かどうかをチェックする
  while (currentElement && currentElement != parentElement) {
    if (isHeightScrollable(currentElement)) {
      return true;
    }
    currentElement = currentElement.parentElement;
  }

  // 最終的に親要素自体がスクロール可能かどうかをチェックする
  return isHeightScrollable(parentElement);
}

/**
 * 縦スクロールを横スクロールとして処理します
 * @param {*} event - WheelEvent オブジェクト
 */
function handleWheelForHorizontalScroll(event) {
  // 水平方向へのスクロール量が大きい場合は、既存の横スクロールを優先
  if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

  // マウスが指している要素が縦スクロール可能なエリアに含まれているか確認
  const mouseHoverElement = document.elementFromPoint(event.clientX, event.clientY);
  if (isChildInScrollableParent(mouseHoverElement, scrollViewport)) return;

  // デフォルトの縦スクロール動作を停止
  event.preventDefault();

  // 縦方向のスクロール量 (deltaY) を横方向のスクロール位置 (scrollLeft) に加算
  scrollViewport.scrollLeft += event.deltaY;
}

/**
 * カテゴリクリック時に、カテゴリ情報に記載されたURLからメタタグ情報を取得を取得し、URLを取得した情報へ置き換えます
 * @param {*} element - メタタグ情報取得先のURL
 */
function getMetaInfo(element) {
  // 要素を展開している状態：カテゴリの詳細はmaxWidth != ""、本はdisplay == "block"
  // const isOpen = element.style.maxWidth != "" || element.style.display == "block"
  // if(!isOpen) return;

  element.querySelectorAll('a[data-meta-tag="true"]').forEach((aTagElement) => {
    const url = aTagElement.getAttribute("href");

    // Ajaxでデータを取得
    $.ajax({
      type: "PATCH",
      url: "/categories/get_meta_info",
      dataType: "script",
      data: { url: url },
      beforeSend: function () {
        // リクエスト送信前の処理
      },
      success: function () {
        // 成功時の処理：aタグを取得したメタ情報に置き換える
        aTagElement.outerHTML = meta_info;
      },
      error: function (jqXHR, textStatus, errorThrown) {
        // エラー発生時の処理
        console.error("Error:", textStatus, errorThrown);
        // console.log("Response:", jqXHR.responseText); // レスポンスを確認
      },
      complete: function () {},
    });
  });
}

/**
 * カテゴリの詳細に配置された検索フォームのクリックイベントを設定します
 * @param {HTMLElement} categoryElement - 検索に設定された.category要素
 */
function setClickEventSearchForm(categoryElement) {
  const searchForm = categoryElement?.querySelector("#search-form");
  const searchFormSubmit = categoryElement?.querySelector("#search-form-submit");

  if (!searchForm || !searchFormSubmit) return;

  searchFormSubmit.addEventListener("click", function (event) {
    event.preventDefault();

    const formData = new FormData(searchForm);

    const search_word = formData.get("search_word");
    // 検索ワードが空の場合は検索しない
    const errMsg = categoryElement?.querySelector(".search-err-message");
    if (search_word.trim() === "") {
      errMsg.innerHTML = "検索ワードを入力してください";
      return;
    } else {
      errMsg.innerHTML = "";
    }

    const search_target = formData.get("search_target");

    // 索引検索結果をクリア
    categoryElement.querySelectorAll(":scope > .category").forEach((e) => {
      e.remove();
    });

    // 本検索結果をクリア
    const eleBooks = categoryElement.querySelector(".books");
    eleBooks.innerHTML = "";

    const hold_available = container.getAttribute("hold-available");

    const restrictedAccess = container.getAttribute("restricted-access") === "true";
    // restrictedAccessがtrueの場合、検索対象をdocumentのload-category-idにする
    const in_category_id = restrictedAccess ? container.getAttribute("load-category-id") : undefined;

    const community_category_id = categoryElement.getAttribute("data-community-category-id");

    // ローディング開始
    util.showLoader();

    $.ajax({
      url: "search_result",
      type: "POST",
      data: {
        search_word: search_word,
        search_target: search_target,
        hold_available: hold_available,
        in_category_id: in_category_id,
        community_category_id: community_category_id,
      },
      dataType: "script",
      success: function () {
        const categoryInfoEle = categoryElement.querySelector(".category-info");
        categoryInfoEle.insertAdjacentHTML("afterend", catesHTML);
        categoryElement.querySelectorAll(":scope > .category").forEach((e) => {
          e.style.display = "flex";
        });

        eleBooks.innerHTML = booksHTML;
        const innerCategoryElement = categoryElement.querySelectorAll(".category");

        // 検索結果の索引にクリックイベントを設定
        setClickEventCategory(innerCategoryElement);
        // 検索結果の本にクリックイベントを設定
        setClickEventBook(eleBooks);
        // 検索結果の内部リンクにクリックイベントを設定
        setClickEventInternalLinks(categoryInfoEle, eleBooks);

        handleResize();
      },
      error: function (data) {
        // エラー発生時の処理
        console.error("Error: Search process failed.");
      },
      complete: function () {
        // ローディング終了
        util.hideLoader();
      },
    });
  });
}

/**
 * 本のクリック時に本の追加情報を取得します。
 * 追加で取得する情報はいかのとおりです。
 * ・メタタグ情報
 * ・愛読者・推薦者のリンク
 * @param {*} element - 本の詳細情報が含まれる要素
 */
function fetchBookExtras(bookElement, bookDetailElement) {
  // 要素を展開している状態：本の詳細はdisplay == "block"
  const isOpen = bookDetailElement.style.display == "block";
  if (!isOpen) return;

  let currentElement = bookElement;
  while (currentElement && !currentElement.classList.contains("category")) {
    currentElement = currentElement.parentElement;
  }

  const category_id = currentElement?.getAttribute("data-category-id") || null;
  const start_category_id = bookElement.getAttribute("data-community-category-id") || null;
  const id_path = currentElement?.getAttribute("id") || null;

  // Ajaxでデータを取得
  $.ajax({
    type: "GET",
    url: "/books/get_book_extras",
    dataType: "json",
    data: {
      book_id: bookElement.getAttribute("data-book-id"),
      category_id: category_id,
      start_category_id: start_category_id,
      id_path: id_path
    },
    beforeSend: function () {
      // リクエスト送信前の処理
    },
    success: function (data) {
      console.log(data);
    },
    error: function (jqXHR, textStatus, errorThrown) {
      // エラー発生時の処理
      console.error("Error:", textStatus, errorThrown);
    },
    complete: function () {}
  });
}